import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Users, AlertCircle } from 'lucide-react';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import CloseButton from '@components/common/CloseButton';
import TierBadge from '@components/common/TierBadge';
import AssessorSelector from '@components/common/AssessorSelector';
import ModalLoadingOverlay from '@components/common/ModalLoadingOverlay';
import type { AssessorOption } from '@components/common/AssessorSelector';
import { RZTable } from '@components/common/table';
import { userService } from '@services';
import { ScheduleItem, TableColumn, Contract, ContractState } from '@app-types';
import { getStatusLabel } from '@utils/statusUtils';
import { formatPersonName } from '@utils/stringUtils';
import { contractStateToVariant, tierToVariant } from '@utils/badgeMappers';
import styles from './GroupContractsModal.module.css';

export interface GroupContractsModalProps {
  selectedContracts: ScheduleItem[];
  onClose: () => void;
  onGroup: (contractIds: number[], assessorId: number | null) => Promise<void>;
}

// Column configuration for contracts table
const contractsTableColumns: TableColumn[] = [
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'contract_number', label: 'Contract #', defaultVisible: true },
  { key: 'tier', label: 'Tier', defaultVisible: true },
  { key: 'assessor', label: 'Assessor', defaultVisible: true }
];

const GroupContractsModal: React.FC<GroupContractsModalProps> = ({ selectedContracts, onClose, onGroup }) => {
  const [selectedAssessorId, setSelectedAssessorId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch assessors
  const { data: assessorsData, isLoading: loadingAssessors } = useQuery({
    queryKey: ['users', 'Assessor'],
    queryFn: () => userService.getUsers('Assessor')
  });

  // Map assessors to AssessorOption format
  const assessors = useMemo<AssessorOption[]>(() => {
    if (!assessorsData) return [];
    return assessorsData.map((assessor) => ({
      assessorId: assessor.id,
      assessorName: formatPersonName(assessor),
      email: assessor.email
    }));
  }, [assessorsData]);

  // Extract contract IDs and validate compatibility
  const contractIds = useMemo(() => {
    return selectedContracts.flatMap((item) => item.contracts.map((c) => c.id));
  }, [selectedContracts]);

  const allContracts = useMemo(() => {
    return selectedContracts.flatMap((item) => item.contracts);
  }, [selectedContracts]);

  // Render function for contracts table
  const renderContractCell = (column: TableColumn, contract: Contract): React.ReactNode => {
    const item = selectedContracts.find((i) => i.contracts.some((c) => c.id === contract.id));

    switch (column.key) {
      case 'status': {
        const contractState = (contract.state || 'not scheduled') as ContractState;
        return (
          <Badge variant={contractStateToVariant(contractState)}>
            {getStatusLabel(contract.state || 'not scheduled')}
          </Badge>
        );
      }
      case 'contract_number':
        return <span className={styles.contractNumber}>{contract.contract_number}</span>;
      case 'tier':
        return <TierBadge variant={tierToVariant(String(contract.tier || ''))}>{contract.tier}</TierBadge>;
      case 'assessor':
        return <span className={styles.assessorName}>{item?.assessor?.name || 'Not Assigned'}</span>;
      default:
        return ((contract as unknown as Record<string, unknown>)[column.key] as React.ReactNode) || '-';
    }
  };

  // Check if all contracts have the same assessor
  const commonAssessorId = useMemo(() => {
    if (selectedContracts.length === 0) return null;

    const assessorIds = selectedContracts.map((item) => item.assessor?.id || 0).filter((id) => id > 0);

    // If all selected items have the same assessor ID
    if (assessorIds.length === selectedContracts.length && new Set(assessorIds).size === 1) {
      return assessorIds[0];
    }

    return null;
  }, [selectedContracts]);

  // Auto-select common assessor if all contracts have the same one
  useEffect(() => {
    if (commonAssessorId && commonAssessorId > 0) {
      setSelectedAssessorId(commonAssessorId);
    }
  }, [commonAssessorId]);

  // Calculate group properties
  const groupProperties = useMemo(() => {
    if (allContracts.length === 0 || selectedContracts.length === 0) return null;

    const tiers = new Set(allContracts.map((c) => c.tier));
    const statuses = new Set(allContracts.map((c) => c.status));
    const states = new Set(allContracts.map((c) => c.state));

    // Check assessor compatibility (from parent ScheduleItem)
    const assessorIds = new Set(selectedContracts.map((item) => item.assessor?.id || null));

    // Get common assessor name if all have the same assessor
    let assessorName = 'Not Assigned';
    if (assessorIds.size === 1) {
      const firstAssessor = selectedContracts.find((item) => item.assessor)?.assessor;
      if (firstAssessor) {
        assessorName = firstAssessor.name;
      }
    } else if (assessorIds.size > 1) {
      assessorName = 'Mixed';
    }

    // Check contract managers compatibility (comparing sorted arrays)
    const managersSets = allContracts.map((c) => {
      const managers = c.contract_managers || [];
      return JSON.stringify(managers.map((m) => m.id).sort((a, b) => a - b));
    });
    const uniqueManagersSets = new Set(managersSets);

    return {
      tier: tiers.size === 1 ? Array.from(tiers)[0] : 'Mixed',
      status: statuses.size === 1 ? Array.from(statuses)[0] : 'Mixed',
      state: states.size === 1 ? Array.from(states)[0] : 'Mixed',
      assessor: assessorName,
      contractCount: allContracts.length,
      isCompatible: statuses.size === 1 && assessorIds.size === 1 && uniqueManagersSets.size === 1
    };
  }, [allContracts, selectedContracts]);

  const handleCombine = async () => {
    if (!groupProperties?.isCompatible) {
      setError('All contracts must have the same status, assessor, and contract managers to be grouped');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onGroup(contractIds, selectedAssessorId);
    } catch (error) {
      setError('Failed to group contracts. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!groupProperties) {
    return null;
  }

  return (
    <div className={styles.overlay} data-test-id="group-contracts-modal">
      <div className={styles.modal}>
        {/* Loading Overlay */}
        {isSubmitting && <ModalLoadingOverlay message="Combining contracts..." />}

        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <Users size={24} color="#3b82f6" />
            </div>
            <div className={styles.headerText}>
              <h3 data-test-id="group-contracts-title">Combine Contracts to Group</h3>
              <p>Create a grouped schedule item for multiple contracts</p>
            </div>
          </div>
          <CloseButton onClick={onClose} />
        </div>

        <div className={styles.content}>
          {/* Group Properties Summary */}
          <div className={styles.properties}>
            <h5>Group Properties:</h5>
            <div className={styles.propertiesGrid}>
              <div className={styles.propertyItem}>
                <span className={styles.propertyLabel}>Tier:</span>
                <div className={styles.propertyValue}>
                  {groupProperties.tier === 'Mixed' ? (
                    <span className={styles.propertyValueMixed}>Mixed</span>
                  ) : (
                    <TierBadge variant={tierToVariant(groupProperties.tier)}>{groupProperties.tier}</TierBadge>
                  )}
                </div>
              </div>
              <div className={styles.propertyItem}>
                <span className={styles.propertyLabel}>Assessor:</span>
                <span
                  className={clsx(
                    styles.propertyValue,
                    (groupProperties.assessor === 'Mixed' || groupProperties.assessor === 'Not Assigned') &&
                      styles.propertyValueMixed
                  )}
                >
                  {groupProperties.assessor}
                </span>
              </div>
              <div className={styles.propertyItem}>
                <span className={styles.propertyLabel}>Status:</span>
                <span
                  className={clsx(
                    styles.propertyValue,
                    groupProperties.status === 'Mixed' && styles.propertyValueMixed
                  )}
                >
                  {groupProperties.status}
                </span>
              </div>
              <div className={styles.propertyItem}>
                <span className={styles.propertyLabel}>Contracts in group:</span>
                <span className={styles.propertyValue}>{groupProperties.contractCount}</span>
              </div>
            </div>
          </div>

          {/* Compatibility Warning */}
          {!groupProperties.isCompatible && (
            <div className={styles.warningMessage}>
              <AlertCircle size={16} />
              <span>
                Warning: Contracts must have the same status, assessor, and contract managers to be grouped.
              </span>
            </div>
          )}

          {/* Contracts List */}
          <div className={styles.contractsSection}>
            <h5>Selected Contracts ({allContracts.length}):</h5>
            <div>
              <RZTable
                data={allContracts as unknown as Contract[]}
                columns={contractsTableColumns}
                getRowKey={(contract) => contract.id}
                renderCell={renderContractCell}
                state={{
                  isLoading: false,
                  isEmpty: false
                }}
              />
            </div>
          </div>

          {/* Assessor Selection */}
          <div className={styles.assessorSelection}>
            <h5>
              Select Assessor for Group <span className={styles.optionalLabel}>(optional)</span>
            </h5>
            <AssessorSelector
              assessors={assessors}
              selectedAssessorId={selectedAssessorId}
              onSelectAssessor={setSelectedAssessorId}
              isLoading={loadingAssessors}
              disabled={isSubmitting}
              showWorkload={false}
              layout="grid"
              emptyMessage="No assessors available"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button variant="primary" onClick={handleCombine} disabled={!groupProperties.isCompatible || isSubmitting}>
            {isSubmitting ? 'Combining...' : 'Combine Contracts'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GroupContractsModal;
